import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Stack, Grid, Chip, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService } from '../../services/optionsService';
import { formatISTDateTime } from '../../utils/utils';
import HistoryIcon from '@mui/icons-material/History';

const StrategyVersionsPage: React.FC = () => {
  const [versions, setVersions] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  const [readiness, setReadiness] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    optionsService
      .getStrategyVersions()
      .then((data) => {
        setVersions(data.versions);
        setSelected(data.versions[0] || null);
        setLoadError(null);
      })
      .catch((err) => setLoadError(err?.message || 'Failed to load registered strategy versions'));
  }, []);

  useEffect(() => {
    if (!selected) return;
    optionsService
      .getReadiness(selected.strategyName, selected.version)
      .then(setReadiness)
      .catch(() => setReadiness(null));
  }, [selected]);

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Grid container spacing={2.5}>
        {/* Version Registry */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <HistoryIcon sx={{ color: '#0284C7' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                STRATEGY VERSION REGISTRY
              </Typography>
            </Stack>

            {loadError && (
              <Typography variant="body2" sx={{ color: '#DC2626' }}>
                {loadError}
              </Typography>
            )}
            {!loadError && versions === null && (
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Loading registered strategy manifests…
              </Typography>
            )}
            {versions?.length === 0 && (
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                No strategy manifests registered yet under config/strategies.
              </Typography>
            )}

            <Stack spacing={1.5}>
              {(versions || []).map((v) => (
                <Paper
                  key={`${v.strategyName}_${v.version}`}
                  elevation={0}
                  onClick={() => setSelected(v)}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: selected?.version === v.version && selected?.strategyName === v.strategyName ? '2px solid #0284C7' : '1px solid #E2E8F0',
                    bgcolor: selected?.version === v.version && selected?.strategyName === v.strategyName ? '#F0F9FF' : '#FFFFFF',
                    cursor: 'pointer',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {v.strategyName} v{v.version}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Commit {v.gitCommit || '—'} · Engine {v.engineVersion || '—'} · {v.createdAt ? formatISTDateTime(v.createdAt) : '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Live Qualification Snapshot for Selected Version */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
              {selected ? `LIVE QUALIFICATION: ${selected.strategyName} v${selected.version}` : 'SELECT A VERSION'}
            </Typography>

            {selected && !readiness && (
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Evaluating readiness against live backtest data…
              </Typography>
            )}

            {readiness && (
              <>
                <Chip
                  size="small"
                  label={readiness.overallDeploymentVerdict}
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    bgcolor: readiness.overallDeploymentVerdict === 'APPROVED_FOR_LIVE' ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.15)',
                    color: readiness.overallDeploymentVerdict === 'APPROVED_FOR_LIVE' ? '#16A34A' : '#D97706',
                  }}
                />
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8FAFC', '& th': { fontWeight: 600, color: '#64748B', fontSize: '0.7rem' } }}>
                      <TableCell>GATE</TableCell>
                      <TableCell align="right">RESULT</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: 'Historical Performance', gate: readiness.historicalGate, val: `PF ${readiness.historicalGate?.profitFactor?.toFixed(2)}, Win ${readiness.historicalGate?.winRate?.toFixed(2)}%` },
                      { name: 'Data Quality', gate: readiness.dataQualityGate, val: `${readiness.dataQualityGate?.qualityScorePct?.toFixed(2)}%` },
                      { name: 'Monte Carlo Tail Risk', gate: readiness.monteCarloGate, val: `Ruin ${readiness.monteCarloGate?.ruinProbPct?.toFixed(2)}%` },
                      { name: 'Catastrophic Stress', gate: readiness.catastrophicStressGate, val: `${readiness.catastrophicStressGate?.scenariosPassed}/8` },
                      { name: 'Paper Realism', gate: readiness.paperRealismGate, val: `${readiness.paperRealismGate?.realismScorePct?.toFixed(1)}%` },
                    ].map((row) => (
                      <TableRow key={row.name}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: row.gate?.passed ? '#16A34A' : '#DC2626' }}>
                          {row.gate?.passed ? '✅' : '❌'} {row.val}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}

            {versions && versions.length <= 1 && (
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 2 }}>
                No challenger versions are registered yet — champion/challenger comparison will appear here once a second manifest is added under config/strategies.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StrategyVersionsPage;
